import { useEffect, useState, useMemo, useRef } from "react";
import { api, formatINR, describeAxiosError } from "@/lib/api";
import { Loader2, Cog, X, Save, FileSpreadsheet, RefreshCw } from "lucide-react";
import { UpdatePfTotalsDialog } from "@/components/payroll/UpdatePfTotalsDialog";
import { PayrollRegister } from "@/components/payroll/PayrollRegister";
import { SalarySlipModal } from "@/components/payroll/SalarySlipModal";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatMonthLabel = (m) => {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "";
  const [y, mm] = m.split("-");
  return `${MONTH_NAMES[parseInt(mm, 10) - 1]} ${y}`;
};

export default function Payroll() {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [company, setCompany] = useState("");
  const [showPfUpdate, setShowPfUpdate] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const requestSequence = useRef(0);
  const pendingPf = items.filter((p) => p.epf_employer > 0 && p.deduction_policy_version !== 2).length;

  const load = async (m, c = company) => {
    setLoading(true);
    const sequence = ++requestSequence.current;
    const params = m ? { month: m } : {};
    if (c) params.company = c;
    setError("");
    try { const { data } = await api.get("/payroll", { params }); if (sequence === requestSequence.current) setItems(data); }
    catch (e) { if (sequence === requestSequence.current) setError(describeAxiosError(e)); }
    finally { if (sequence === requestSequence.current) setLoading(false); }
  };

  useEffect(() => {
    const request = new AbortController();
    api.get("/payroll/months", { signal: request.signal }).then((r) => {
      if (request.signal.aborted) return;
      setMonths(r.data);
      const currentMonth = new Date().toISOString().slice(0, 7);
      const initial = r.data.includes(currentMonth) ? currentMonth : (r.data[0] || "");
      if (initial) { setMonth(initial); load(initial); }
      else setLoading(false);
    }).catch((e) => { if (!request.signal.aborted) { setError(describeAxiosError(e)); setLoading(false); } });
    return () => request.abort();
  }, []);

  const processNew = async () => {
    const now = new Date();
    const newMonth = now.toISOString().slice(0, 7);
    await api.post("/payroll/process", { month: newMonth });
    const r = await api.get("/payroll/months");
    setMonths(r.data); setMonth(newMonth); load(newMonth);
  };

  const totals = useMemo(() => items.reduce((a, p) => ({
    fixed: a.fixed + (p.fixed_gross || 0),
    earned: a.earned + (p.earned_gross ?? p.gross ?? 0),
    ded: a.ded + (p.total_deductions || 0),
    net: a.net + (p.net_take_home ?? p.net_pay ?? 0),
  }), { fixed: 0, earned: 0, ded: 0, net: 0 }), [items]);

  const recompute = async (row) => {
    if (!window.confirm(`Recompute ${row.employee_name}'s salary for ${row.month}? Saved paid days and manual deductions stay unchanged.`)) return;
    try { await api.post(`/payroll/${row.id}/recompute`); await load(month); }
    catch (e) { setError(describeAxiosError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <header className="space-y-4" data-testid="payroll-header">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Salary Register</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1" data-testid="payroll-title">Payroll</h1>
          <p className="text-stone-600 mt-1 text-sm" data-testid="payroll-deduction-basis">Total deductions include both PF shares. Employer PF already excluded from gross is not deducted twice. Employer ESI remains excluded.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1">
            <span className="kpi-label">Company</span>
            <select value={company} onChange={(e) => { setCompany(e.target.value); load(month, e.target.value); }}
                    className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[10rem]" data-testid="payroll-company-filter">
              <option value="">All Companies</option>
              <option value="NAVARATNAA">Navaratnaa</option>
              <option value="SHERATAAN">Sherataan</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="kpi-label">Month</span>
            <select value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
                    className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[10rem]" data-testid="payroll-month">
              {months.length === 0 && <option value="">No months available</option>}
              {months.map((m) => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
            </select>
          </label>
          <button onClick={async () => {
            const params = { month };
            if (company) params.company = company;
            const { data } = await api.get("/payroll/export.xlsx", { params, responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `salary_register_${month}${company ? '_' + company : ''}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
          }} data-testid="export-payroll-xlsx" disabled={loading || !month || pendingPf > 0} title={pendingPf ? "Update PF totals before exporting this month" : "Export salary register"}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium text-sm" style={{ background: "#1B4332" }}>
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </button>
          <button onClick={processNew} data-testid="process-payroll-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium text-sm" style={{ background: "var(--color-secondary)" }}>
            <Cog className="w-4 h-4" /> Process Current
          </button>
          <button onClick={async () => {
            const m = month || new Date().toISOString().slice(0, 7);
            const { data } = await api.post("/payroll/auto-fill-from-attendance", { month: m });
            alert(`Auto-filled from attendance: ${data.created} created, ${data.updated} updated.`);
            load(m);
          }} data-testid="autofill-payroll-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium text-sm" style={{ background: "var(--color-primary)" }}>
            <Cog className="w-4 h-4" /> Auto-Fill from Attendance
          </button>
        </div>
      </header>

      {error && <p role="alert" className="text-sm text-red-700" data-testid="payroll-error">{error}</p>}
      {notice && <p role="status" className="text-sm text-primary" data-testid="payroll-update-notice">{notice}</p>}
      {!loading && pendingPf > 0 && <div className="flex flex-wrap items-center justify-between gap-3 border-l-4 border-amber-600 bg-amber-50 px-4 py-3" data-testid="payroll-pf-update-banner"><p className="text-sm text-amber-900" data-testid="payroll-pf-pending-count">{pendingPf} saved PF record(s) need the combined deduction total.</p><button className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline" onClick={() => setShowPfUpdate(true)} data-testid="update-pf-totals-button"><RefreshCw className="w-4 h-4" />Update PF totals</button></div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi label="Fixed Gross" v={formatINR(totals.fixed)} />
        <Kpi label="Earned Gross" v={formatINR(totals.earned)} accent="#1B4332" bg="#D8F3DC" />
        <Kpi label="Total Deductions" v={formatINR(totals.ded)} accent="#9A3412" bg="#FADED7" />
        <Kpi label="Net Take-Home" v={formatINR(totals.net)} accent="#1B4332" bg="#D8F3DC" />
      </div>

      <div className="min-w-0">
        {loading ? <div className="flex items-center justify-center py-24" data-testid="payroll-loading"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : !items.length ? <p className="py-12 text-center text-stone-500" data-testid="payroll-empty">No records for {formatMonthLabel(month) || "this month"}.</p> : <PayrollRegister items={items} onEdit={setEditing} onSlip={setSelected} onRecompute={recompute} />}
      </div>

      {selected && <SalarySlipModal data={selected} onClose={() => setSelected(null)} />}
      {showPfUpdate && <UpdatePfTotalsDialog month={month} company={company} count={pendingPf} onClose={() => setShowPfUpdate(false)} onSaved={(result) => { setShowPfUpdate(false); setNotice(`${result.updated} PF record(s) updated. Employer ESI and other months are unchanged.`); load(month); }} />}
      {editing && <EditPayrollModal record={editing} onClose={() => setEditing(null)}
                                     onSaved={() => { setEditing(null); load(month); }} />}
    </div>
  );
}

function Kpi({ label, v, accent, bg }) {
  return (
    <div className="card-flat min-w-0" style={{ background: bg || "white", padding: 16 }} data-testid={`payroll-kpi-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="kpi-label">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1 break-all" style={{ color: accent || "var(--text-primary)" }}>{v}</div>
    </div>
  );
}

function EditPayrollModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState({
    paid_days: record.paid_days ?? record.month_days ?? 30,
    salary_advance: record.salary_advance || 0,
    insurance: record.insurance || 0,
    others: record.others || 0,
    remarks: record.remarks || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true); setError("");
    try { await api.put(`/payroll/${record.id}`, {
      paid_days: Number(form.paid_days),
      salary_advance: Number(form.salary_advance),
      insurance: Number(form.insurance),
      others: Number(form.others),
      remarks: form.remarks,
    });
    onSaved(); }
    catch (e) { setError(describeAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4" data-testid="edit-payroll-modal">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="kpi-label">Adjust · {record.month}</div>
            <h2 className="font-heading text-xl font-bold mt-1">{record.employee_name}</h2>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close payroll editor" data-testid="edit-payroll-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <In label={`Paid Days (of ${record.month_days || 30})`} type="number" v={form.paid_days} on={(v) => setForm({ ...form, paid_days: v })} />
            <In label="Salary Advance ₹" type="number" v={form.salary_advance} on={(v) => setForm({ ...form, salary_advance: v })} />
            <In label="Insurance ₹" type="number" v={form.insurance} on={(v) => setForm({ ...form, insurance: v })} />
            <In label="Others ₹" type="number" v={form.others} on={(v) => setForm({ ...form, others: v })} />
          </div>
          <label className="block">
            <div className="kpi-label mb-1.5">Remarks</div>
            <textarea value={form.remarks} rows="2" onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="payroll-edit-remarks"
                      className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
          </label>
          {error && <p role="alert" className="text-sm text-red-700" data-testid="payroll-edit-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="save-payroll-button"
                  className="w-full py-2 rounded-md text-white font-semibold flex items-center justify-center gap-2"
                  style={{ background: "var(--color-primary)" }}>
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Recalculate & Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function In({ label, type = "text", v, on }) {
  return (
    <label className="block min-w-0">
      <div className="kpi-label mb-1.5">{label}</div>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)}
             data-testid={`payroll-edit-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
             className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
    </label>
  );
}

